import * as R from 'ramda';
import { CredentialItem } from '../../services/secureIdentityProvider/types';
import { ScopeRequest } from '../../services/secureIdentityProvider';
import { DEFAULT_SUPPORTED_DOCUMENT_TYPES, DocumentType } from '../../services/documentCapture/documentCapture';

/*
 * Get the required document types from a credential item claims
 */
export const getCredentialItemDocumentType = (credentialItem: CredentialItem): string[] => {
  const claims = R.pathOr([], ['constraints', 'claims'], credentialItem);
  const docTypeClaim = R.find(R.propEq('path', 'document.type'), claims);
  const docTypeIs = R.propOr({}, 'is', docTypeClaim);
  const eqValue = R.propOr(null, '$eq', docTypeIs);
  const inValue = R.propOr(null, '$in', docTypeIs);

  // Return the value wrapped in an array, or return the array itself.
  const docTypes = R.isNil(eqValue) ? inValue : [eqValue];

  return docTypes as string[];
};

/*
 * Select, from a list of verifiable credentials, the credentials by their identifier
 */
export const selectCredential = (
  credentialIdentifier: string,
  scopeRequest: ScopeRequest,
): CredentialItem | undefined => {
  const credentialItems = R.path(
    ['components', 'identity', 'request', 'payload', 'credentialItems'],
    scopeRequest,
  ) as CredentialItem[];
  return R.find(R.propEq('identifier', credentialIdentifier), credentialItems);
};

/*
 * Get the supported document types from a scope request.
 * Default to DEFAULT_SUPPORTED_DOCUMENT_TYPES if no credential item is found.
 */
export const findSupportedDocumentTypes = (scopeRequest: ScopeRequest | null): DocumentType[] => {
  if (R.isNil(scopeRequest)) {
    return DEFAULT_SUPPORTED_DOCUMENT_TYPES;
  }
  const idDocCredentialItem = selectCredential('credential-cvc:IdDocument-v3', scopeRequest);
  let docTypes = idDocCredentialItem ? getCredentialItemDocumentType(idDocCredentialItem) : null;
  // If docTypes has items, replace driving_license with driving_licence to ensure we match onfido's name
  // This is because onfido uses driving_licence, but the credential item uses driving_license. The other currently supported types
  // don't have contentions spelling variations. They are national_identity_card, residence_permit and passport.
  // Refer to the DocumentType enum in documentCapture.ts for the full list of supported types.
  if (docTypes) {
    docTypes = docTypes.map((docType) => (docType === 'driving_license' ? 'driving_licence' : docType));
  }
  if (R.isNil(docTypes) || R.isEmpty(docTypes)) {
    docTypes = DEFAULT_SUPPORTED_DOCUMENT_TYPES;
  }
  return docTypes as DocumentType[];
};
